import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import {
  createNextStageTablesAPI,
  createTableAPI,
  deleteTableAPI,
  fetchTournamentTablesByIdAPI,
  syncTournamentTablesByIdAPI,
  upsertTableRoomInformationAPI,
  upsertTableRoomTimeAPI,
} from "./tablesAPI";

const TABLE_TYPE_SORT_ORDER = {
  winners: 1,
  final: 2,
  nextStage: 3,
  qualification: 4,
};

const toMillis = (value, fallback = Number.MAX_SAFE_INTEGER) => {
  if (!value) return fallback;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : fallback;
};

const sortTables = (tables = []) =>
  [...tables].sort((a, b) => {
    const typeA = TABLE_TYPE_SORT_ORDER[a?.tableType] ?? 99;
    const typeB = TABLE_TYPE_SORT_ORDER[b?.tableType] ?? 99;
    if (typeA !== typeB) return typeA - typeB;

    const manualA = a?.timeline?.slotStart ? 0 : 1;
    const manualB = b?.timeline?.slotStart ? 0 : 1;
    if (manualA !== manualB) return manualA - manualB;

    const slotA = toMillis(a?.timeline?.slotStart);
    const slotB = toMillis(b?.timeline?.slotStart);
    if (slotA !== slotB) return slotA - slotB;

    const createdA = toMillis(a?.createdAt, 0);
    const createdB = toMillis(b?.createdAt, 0);
    return createdA - createdB;
  });

const mergeDeltaTables = (existing = [], changed = [], removed = []) => {
  const map = new Map(existing.map((table) => [String(table?._id), table]));

  changed.forEach((table) => {
    const tableId = String(table?._id || "");
    if (!tableId) return;
    map.set(tableId, table);
  });

  removed.forEach((tableId) => {
    map.delete(String(tableId || ""));
  });

  return sortTables(Array.from(map.values()));
};

const initialState = {
  list: [],
  fetchStatus: "idle",
  syncStatus: "idle",
  addStatus: "idle",
  createNextStageStatus: "idle",
  error: null,
  deleteStatus: "idle",
  syncCursorsByKey: {},
  lastSyncDelta: null,
  nextStagePlanByTournament: {},
};

export const fetchTournamentTablesById = createAsyncThunk(
  "tables/fetchTournamentTablesById",
  async (payload, { dispatch, rejectWithValue }) => {
    const normalizedPayload =
      typeof payload === "string"
        ? {
            tournamentId: payload,
            filters: {},
            silent: false,
            syncKey: JSON.stringify({}),
          }
        : {
            tournamentId: payload?.tournamentId,
            filters: payload?.filters || {},
            silent: Boolean(payload?.silent),
            syncKey:
              payload?.syncKey || JSON.stringify(payload?.filters || {}),
          };

    try {
      const data = await fetchTournamentTablesByIdAPI(
        normalizedPayload.tournamentId,
        normalizedPayload.filters,
      );

      if (!normalizedPayload.silent) {
        dispatch(
          showModal({
            message: "تم جلب جميع الجداول بنجاح",
            status: "success",
          }),
        );
      }
      return data;
    } catch (error) {
      if (!normalizedPayload.silent) {
        dispatch(
          showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" }),
        );
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch images",
      );
    }
  },
);

export const syncTournamentTablesById = createAsyncThunk(
  "tables/syncTournamentTablesById",
  async (payload, { dispatch, rejectWithValue }) => {
    const normalizedPayload = {
      tournamentId: payload?.tournamentId,
      filters: payload?.filters || {},
      since: payload?.since || "",
      knownTableIds: Array.isArray(payload?.knownTableIds)
        ? payload.knownTableIds
        : [],
      silent: Boolean(payload?.silent),
      syncKey: payload?.syncKey || JSON.stringify(payload?.filters || {}),
    };

    try {
      const data = await syncTournamentTablesByIdAPI({
        tournamentId: normalizedPayload.tournamentId,
        filters: normalizedPayload.filters,
        since: normalizedPayload.since,
        knownTableIds: normalizedPayload.knownTableIds,
      });

      if (!normalizedPayload.silent) {
        dispatch(
          showModal({
            message: "تم تحديث الجداول بنجاح",
            status: "success",
          }),
        );
      }

      return data;
    } catch (error) {
      if (!normalizedPayload.silent) {
        dispatch(
          showModal({ message: "حصل خطأ اثناء تحديث الجداول", status: "error" }),
        );
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to sync tournament tables",
      );
    }
  },
);

export const createTable = createAsyncThunk(
  "tables/createTable",
  async (tableData, { dispatch, rejectWithValue }) => {
    try {
      const data = await createTableAPI(tableData);
      dispatch(
        showModal({
          message: "تم انشاء جدول جديد بنجاح",
          status: "success",
        })
      );
      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء انشاء الجدول", status: "error" })
      );
      console.log(error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch images"
      );
    }
  }
);

export const createNextStageTables = createAsyncThunk(
  "tables/createNextStageTables",
  async (
    { tournamentId, targetTableCount = 15, playersLimit = 60 },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const data = await createNextStageTablesAPI({
        tournamentId,
        targetTableCount,
        playersLimit,
      });
      dispatch(
        showModal({
          message: "تم تجهيز جداول المرحلة المتقدمة بنجاح",
          status: "success",
        }),
      );
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء تجهيز جداول المرحلة المتقدمة",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to prepare next stage tables",
      );
    }
  },
);

export const deleteTable = createAsyncThunk(
  "tables/deleteTable",
  async (tableId, { dispatch, rejectWithValue }) => {
    try {
      await deleteTableAPI(tableId);
      dispatch(
        showModal({ message: "تم حذف الجدول بنجاح", status: "success" })
      );
      return { id: tableId };
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء حذف الجدول", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete table"
      );
    }
  }
);

export const upsertTableRoomInformation = createAsyncThunk(
  "tables/upsertTableRoomInformation",
  async ({ tournamentId, tableId, roomInfo }, { dispatch, rejectWithValue }) => {
    try {
      const data = await upsertTableRoomInformationAPI({
        tournamentId,
        tableId,
        roomInfo,
      });

      const mode = data?.data?.mode;
      dispatch(
        showModal({
          message:
            mode === "created"
              ? "تم إضافة معلومات الروم وإرسال اشعار للاعبين"
              : "تم تحديث معلومات الروم بنجاح",
          status: "success",
        }),
      );

      return data;
    } catch (error) {
      const backendMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save room information";
      dispatch(
        showModal({
          message:
            backendMessage === "Failed to save room information"
              ? "حصل خطأ اثناء حفظ معلومات الروم"
              : backendMessage,
          status: "error",
        }),
      );
      return rejectWithValue(backendMessage);
    }
  },
);

export const upsertTableRoomTime = createAsyncThunk(
  "tables/upsertTableRoomTime",
  async ({ tournamentId, tableId, eventAtUtc, date, timelineSlot }, { dispatch, rejectWithValue }) => {
    try {
      const data = await upsertTableRoomTimeAPI({
        tournamentId,
        tableId,
        eventAtUtc,
        date,
        timelineSlot,
      });

      const mode = data?.data?.mode;
      dispatch(
        showModal({
          message:
            mode === "created"
              ? "تم إضافة موعد الروم وإرسال اشعار للاعبين"
              : "تم تحديث موعد الروم بنجاح",
          status: "success",
        }),
      );

      return data;
    } catch (error) {
      const backendMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save room time";
      dispatch(
        showModal({
          message:
            backendMessage === "Failed to save room time"
              ? "حصل خطأ اثناء حفظ موعد الروم"
              : backendMessage,
          status: "error",
        }),
      );
      return rejectWithValue(backendMessage);
    }
  },
);

const tablesSlice = createSlice({
  name: "tables",
  initialState,
  reducers: {
    setTournamentNextStagePlan: (state, action) => {
      const tournamentId = String(action.payload?.tournamentId || "").trim();
      if (!tournamentId) return;
      state.nextStagePlanByTournament[tournamentId] =
        action.payload?.nextStagePlan || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTournamentTablesById.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTournamentTablesById.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.list = sortTables(action.payload.data || []);
        const tournamentId =
          typeof action.meta?.arg === "string"
            ? action.meta.arg
            : action.meta?.arg?.tournamentId;
        if (tournamentId) {
          state.nextStagePlanByTournament[String(tournamentId)] =
            action.payload?.meta?.nextStagePlan || null;
        }
        const syncKey =
          action.meta?.arg?.syncKey ||
          JSON.stringify(action.meta?.arg?.filters || {});
        const syncToken = action.payload?.meta?.syncToken;
        if (syncKey && syncToken) {
          state.syncCursorsByKey[syncKey] = syncToken;
        }
        state.lastSyncDelta = {
          syncKey,
          changedTableIds:
            action.payload?.meta?.changedTableIds ||
            (action.payload?.data || []).map((table) => String(table?._id || "")),
          removedTableIds: action.payload?.meta?.removedTableIds || [],
          mode: action.payload?.meta?.mode || "full",
        };
      })
      .addCase(fetchTournamentTablesById.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(syncTournamentTablesById.pending, (state) => {
        state.syncStatus = "loading";
        state.error = null;
      })
      .addCase(syncTournamentTablesById.fulfilled, (state, action) => {
        state.syncStatus = "succeeded";
        const tournamentId = action.meta?.arg?.tournamentId;
        if (tournamentId) {
          state.nextStagePlanByTournament[String(tournamentId)] =
            action.payload?.meta?.nextStagePlan || null;
        }

        const syncKey =
          action.meta?.arg?.syncKey ||
          JSON.stringify(action.meta?.arg?.filters || {});
        const syncToken = action.payload?.meta?.syncToken;
        if (syncKey && syncToken) {
          state.syncCursorsByKey[syncKey] = syncToken;
        }

        const mode = action.payload?.meta?.mode || "delta";
        const changedTables = action.payload?.data || [];
        const changedTableIds = action.payload?.meta?.changedTableIds || [];
        const removedTableIds = action.payload?.meta?.removedTableIds || [];

        state.list =
          mode === "full"
            ? sortTables(changedTables)
            : mergeDeltaTables(state.list, changedTables, removedTableIds);

        state.lastSyncDelta = {
          syncKey,
          changedTableIds,
          removedTableIds,
          mode,
        };
      })
      .addCase(syncTournamentTablesById.rejected, (state, action) => {
        state.syncStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(createTable.pending, (state) => {
        state.addStatus = "loading";
        state.error = null;
      })
      .addCase(createTable.fulfilled, (state, action) => {
        state.addStatus = "succeeded";
        state.list = sortTables([...state.list, action.payload.data]);
      })
      .addCase(createTable.rejected, (state, action) => {
        state.addStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(createNextStageTables.pending, (state) => {
        state.createNextStageStatus = "loading";
        state.error = null;
      })
      .addCase(createNextStageTables.fulfilled, (state, action) => {
        state.createNextStageStatus = "succeeded";
        const tournamentId = String(
          action.meta?.arg?.tournamentId || "",
        ).trim();
        const payloadData = action.payload?.data || {};
        const nextStageTables = Array.isArray(payloadData?.nextStageTables)
          ? payloadData.nextStageTables
          : [];

        if (nextStageTables.length) {
          const tableMap = new Map(
            (state.list || []).map((table) => [String(table?._id || ""), table]),
          );
          nextStageTables.forEach((table) => {
            const tableId = String(table?._id || "").trim();
            if (!tableId) return;
            tableMap.set(tableId, {
              ...(tableMap.get(tableId) || {}),
              ...table,
            });
          });
          state.list = sortTables(Array.from(tableMap.values()));
        }

        if (tournamentId) {
          state.nextStagePlanByTournament[tournamentId] =
            payloadData?.nextStagePlan || null;
        }
      })
      .addCase(createNextStageTables.rejected, (state, action) => {
        state.createNextStageStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(deleteTable.pending, (state) => {
        state.error = null;
        state.deleteStatus = "loading";
      })
      .addCase(deleteTable.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (table) => table._id !== action.payload.id
        );
        state.deleteStatus = "succeeded";
      })
      .addCase(deleteTable.rejected, (state, action) => {
        state.error = action.payload;
        state.deleteStatus = "failed";
      })
      .addCase(upsertTableRoomInformation.fulfilled, (state, action) => {
        const tableId = String(action.payload?.data?.tableId || "").trim();
        if (!tableId) return;

        const roomInformation = action.payload?.data?.roomInformation || null;
        state.list = state.list.map((table) =>
          String(table?._id || "") === tableId
            ? {
                ...table,
                roomInformation,
              }
            : table,
        );
      })
      .addCase(upsertTableRoomInformation.rejected, (state, action) => {
        state.error = action.payload || action.error?.message;
      })
      .addCase(upsertTableRoomTime.fulfilled, (state, action) => {
        const tableId = String(action.payload?.data?.tableId || "").trim();
        if (!tableId) return;

        const roomInformation = action.payload?.data?.roomInformation || null;
        const timeline = action.payload?.data?.timeline || null;
        state.list = state.list.map((table) =>
          String(table?._id || "") === tableId
            ? {
                ...table,
                roomInformation,
                ...(timeline ? { timeline } : {}),
              }
            : table,
        );
      })
      .addCase(upsertTableRoomTime.rejected, (state, action) => {
        state.error = action.payload || action.error?.message;
      });
  },
});

export const { setTournamentNextStagePlan } = tablesSlice.actions;

export default tablesSlice.reducer;
